import * as React from 'react';
import {noop} from 'lodash';
import {SetOptional} from 'type-fest';

import {SegmentedControlView} from './SegmentedControl.styles';
import type {HTMLAttributes, SpringTooling} from '../__SharedTypes';
import {
  SelectionModeMultiple,
  SelectionModeSingle,
  useSelectionMode,
} from '../../hooks';

export type KeyboardActivation = 'auto' | 'manual';

type SegmentedControlSelectionModeProps =
  | Pick<
      SetOptional<SelectionModeSingle, 'selectionMode'>,
      'onChange' | 'selectionMode' | 'value'
    >
  | Pick<SelectionModeMultiple, 'onChange' | 'selectionMode' | 'value'>;

export type SegmentedControlProps = {
  /**
   * Content for SegmentedControl.
   */
  children?: React.ReactNode;
  /**
   * Whether the entire `SegmentedControl` and its `SegmentedControlButton`s are disabled.
   */
  isDisabled?: boolean;
  /**
   * Whether the `SegmentedControl` takes up the full width of its parent.
   */
  isFullWidth?: boolean;
  /**
   * Whether `SegmentedControlButtons` are selected automatically on focus or manually.
   */
  keyboardActivation?: KeyboardActivation;
} & SegmentedControlSelectionModeProps &
  Omit<HTMLAttributes, 'onChange'> &
  SpringTooling;

export const SegmentedControlContext = React.createContext<{
  isDisabled?: boolean;
  getIsSelected: (value: string) => boolean;
  hasValue?: boolean;
  keyboardActivation: KeyboardActivation;
  selectionMode: 'single' | 'multiple';
  toggleSelectedValue: (value: string) => void;
}>({
  isDisabled: false,
  getIsSelected: () => false,
  keyboardActivation: 'auto',
  selectionMode: 'single',
  toggleSelectedValue: noop,
});

export const useSegmentedControlContext = () =>
  React.useContext(SegmentedControlContext);

/**
 * # SegmentedControl
 *
 * The SegmentedControl component is used to group buttons together and facilitate single and multi-select.
 *
 * ## Usage
 *
 * ```jsx
 * import {SegmentedControl, SegmentedControlButton} from '@packages/systems/spring';
 *
 * export const Example = () => {
 *   const [value, setValue] = React.useState('');
 *
 *   return (
 *     <SegmentedControl onChange={setValue} selectionMode="single" value={value}>
 *       <SegmentedControlButton value="option-one">
 *         Option 1
 *       </SegmentedControlButton>
 *       <SegmentedControlButton value="option-two">
 *         Option 2
 *       </SegmentedControlButton>
 *       <SegmentedControlButton value="option-three">
 *         Option 3
 *       </SegmentedControlButton>
 *     </SegmentedControl>
 *   );
 * };
 * ```
 *
 * @component
 */
export const SegmentedControl = React.forwardRef<
  HTMLDivElement,
  SegmentedControlProps
>(
  (
    {
      children,
      isDisabled = false,
      isFullWidth = true,
      keyboardActivation = 'auto',
      onChange,
      selectionMode = 'single',
      value,
      ...props
    }: SegmentedControlProps,
    ref
  ) => {
    const {toggleSelectedValue, getIsSelected} =
      // @ts-expect-error - For some reason, typescript can't pass discriminated union types through to discriminated union types. ¯\_(ツ)_/¯
      useSelectionMode({
        onChange,
        selectionMode,
        value,
      });

    const role = selectionMode === 'single' ? 'radiogroup' : 'group';

    return (
      <SegmentedControlContext.Provider
        value={{
          isDisabled,
          getIsSelected,
          hasValue: Boolean(
            value !== null && value !== '' && typeof value !== 'undefined'
          ),
          keyboardActivation,
          selectionMode,
          toggleSelectedValue,
        }}
      >
        <SegmentedControlView
          isFullWidth={isFullWidth}
          role={role}
          {...props}
          ref={ref}
        >
          {children}
        </SegmentedControlView>
      </SegmentedControlContext.Provider>
    );
  }
);

SegmentedControl.displayName = 'SegmentedControl';
SegmentedControl.toString = SegmentedControlView.toString;

export default SegmentedControl;
